import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpErrorResponse } from '@angular/common/http';
import * as moment from 'moment';
import { Observable } from 'rxjs/Observable';
import 'rxjs/add/operator/map';
import 'rxjs/add/operator/catch';
import 'rxjs/add/observable/fromPromise';
import { environment } from '../../environments/environment';
import { ResponseResult, State } from '../core/state';
import { Article, ArticleItem, ArticleType } from '../core/article';

@Injectable()
export class NewsService {
    private readonly rootUrl = environment.apiUrl;
    private readonly url = `${environment.apiUrl}/company/articles`;
    private jsonContentHeader: HttpHeaders = new HttpHeaders({ 'Content-Type': 'application/json' });

    constructor(private http: HttpClient) { }

    get(limit: number, offset: number): Observable<ResponseResult<Article[]>> {
        limit = limit > 0 ? limit : 50;
        offset = offset > 0 ? offset : 0;

        return this.http
            .get(`${this.url}?fields=id,title,image,created&limit=${limit}&offset=${offset}`, { headers: this.jsonContentHeader })
            .map(result => {
                const response: ResponseResult<Article[]> = new ResponseResult<Article[]>();
                if (result['options']) {
                    response.state = result['options'] as State;
                }
                if (result['data']) {
                    response.data = result['data'].map(a => this.wrapArticle(a));
                }

                return response;
            });
    }

    getArticle(id: string): Observable<Article> {
        return this.http
            .get(`${this.url}/${id}`)
            .map(result => {
                const raw = result['data'];
                const article = this.wrapArticle(raw);

                return article;
            });
    }

    publish(newArticleString: string): Observable<any> {
        return this.http
            .post(`${this.url}`, newArticleString, { headers: this.jsonContentHeader })
            .map(result => {
                return result as Article;
            })
            .catch(error => {
                return Observable.fromPromise(this.handleError(error));
            });
    }
    update(id: string, updatedArticleString: string): Observable<any> {
        return this.http
            .put(`${this.url}/${id}`, updatedArticleString, { headers: this.jsonContentHeader })
            .map(result => {
                return result as Article;
            })
            .catch(error => {
                return Observable.fromPromise(this.handleError(error));
            });
    }

    remove(id): Observable<boolean> {
        return this.http
            .delete(`${this.url}/${id}`)
            .map(result => {
                return true;
            });
    }

    private wrapArticle(raw: any): Article {
        const article = new Article();
        article.id = raw.id;
        article.title = raw.title;
        if (raw.articleItems) {
            article.items = this.prepareItems(raw.articleItems);
        }
        article.publishDate = moment(raw.created);
        article.articleDate = moment(raw.articleDate);
        if (raw.image) {
            if (raw.image.startsWith('data:')) {
                article.previewSrc = raw.image;
            } else {
                if (raw.image.startsWith('http')) {
                    const anchor = document.createElement('a');
                    anchor.href = raw.image;

                    raw.image = `${anchor.pathname}`;
                    if (anchor.search) {
                        raw.image = `${raw.image}/${anchor.search}`;
                    }
                    if (anchor.hash) {
                        raw.image = `${raw.image}/${anchor.hash}`;
                    }
                }

                const path = raw.image.startsWith('/') ? raw.image : `/${raw.image}`;
                article.previewSrc = `${this.rootUrl}${path}`;
            }
        }

        return article;
    }
    private prepareItems(rawItems) {
        return rawItems.map(i => {
            const item = new ArticleItem();
            item.id = i._id;
            item.type = i.itemType as ArticleType;
            if (item.type === ArticleType.Image) {
                if (i.itemContent.startsWith('data:')) {
                    item.content = i.itemContent;
                } else {
                    if (i.itemContent.startsWith('http')) {
                        const anchor = document.createElement('a');
                        anchor.href = i.itemContent;

                        i.itemContent = `${anchor.pathname}`;
                        if (anchor.search) {
                            i.itemContent = `${i.itemContent}/${anchor.search}`;
                        }
                        if (anchor.hash) {
                            i.itemContent = `${i.itemContent}/${anchor.hash}`;
                        }
                    }

                    const path = i.itemContent.startsWith('/') ? i.itemContent : `/${i.itemContent}`;
                    item.content = `${this.rootUrl}${path}`;
                }
            } else {
                item.content = i.itemContent;
            }

            return item;
        });
    }

    private handleError(error: HttpErrorResponse): Promise<any> {
        const message = this.resolveError(error);
        if (message) {
            return Promise.reject(message);
        }

        return Promise.reject(error);
    }

    private resolveError(res: HttpErrorResponse): any {
        if (res.error && res.error.errors && res.error.errors.length > 0) {
            const parts = res.error.errors[0].split('.');
            if (parts.length <= 0) {
                return null;
            }

            let result = this;
            for (const part of parts) {
                result = result[part];
                if (!result) {
                    break;
                }
            }

            return result;
        }
    }
}



// WEBPACK FOOTER //
// D:/programming/Чижик_Api_Site_Sources-Electroline/front/src/app/providers/news.service.ts